import { useCallback, useSyncExternalStore } from "react";

/**
 * Darstellungsvorlieben, die kein eigenes Thema sind.
 *
 * Schrift und Phasenstärke gehören nicht in die Palette: sie sind unabhängig
 * davon, welches Thema läuft, und wer von Mocha auf Latte wechselt, will
 * seine Schrift behalten. Sie liegen deshalb neben dem Thema, nicht darin.
 *
 * Beides schreibt in CSS-Eigenschaften am Wurzelelement — die Oberfläche
 * liest sie über `var(--sans)` bzw. `var(--phase-staerke)`, und niemand muss
 * eine Zahl an zwanzig Stellen durchreichen.
 */

export type Schriftwahl = {
  id: string;
  label: string;
  /** Was als `--sans` gesetzt wird. */
  stack: string;
};

/** Nichts wird nachgeladen: eine Auswahl, die beim Anschauen erst etwas aus
 *  dem Netz holt, ist keine Auswahl, sondern ein Ladebalken — und die
 *  Sicherheitsregeln dieser Seite lassen fremde Schriftquellen ohnehin nicht
 *  zu. Jede Zeile ist deshalb ein Stapel: was da ist, wird genommen, sonst
 *  das Nächste. */
export const SCHRIFTEN: Schriftwahl[] = [
  {
    id: "system",
    label: "System",
    stack: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  },
  {
    id: "inter",
    label: "Inter",
    stack: 'Inter, ui-sans-serif, system-ui, sans-serif',
  },
  {
    id: "serif",
    label: "Serif",
    stack: 'ui-serif, Georgia, "Times New Roman", serif',
  },
  // ── Feste Breite ────────────────────────────────────────────────────
  //
  // Mehrere, weil sich Schreibschriften stärker unterscheiden als
  // Fließtextschriften: Ligaturen, Ziffernform, wie deutlich 0 von O zu
  // trennen ist. Jede Zeile ist ein Stapel — steht die genannte Schrift
  // nicht zur Verfügung, greift die nächste, und ganz hinten steht immer
  // `monospace`, damit nie etwas Proportionales herauskommt.
  //
  // Die Nerd-Font-Varianten stehen jeweils vor der gewöhnlichen: sie bringen
  // Powerline- und Rahmenzeichen mit, und genau die braucht ein Terminal.
  {
    id: "mono",
    label: "Mono (system)",
    stack: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  },
  {
    id: "firacode",
    label: "Fira Code",
    stack: '"FiraCode Nerd Font", "Fira Code", "FiraCode Nerd Font Mono", ui-monospace, monospace',
  },
  {
    id: "jetbrains",
    label: "JetBrains Mono",
    stack: '"JetBrainsMono Nerd Font", "JetBrains Mono", ui-monospace, monospace',
  },
  {
    id: "meslo",
    label: "Meslo",
    stack: '"MesloLGS Nerd Font", "MesloLGM Nerd Font", Meslo, Menlo, ui-monospace, monospace',
  },
  {
    id: "hack",
    label: "Hack",
    stack: '"Hack Nerd Font", Hack, "DejaVu Sans Mono", ui-monospace, monospace',
  },
  {
    id: "cascadia",
    label: "Cascadia Code",
    stack: '"CaskaydiaCove Nerd Font", "Cascadia Code", "Cascadia Mono", ui-monospace, monospace',
  },
  {
    id: "ibmplex",
    label: "IBM Plex Mono",
    stack: '"BlexMono Nerd Font", "IBM Plex Mono", ui-monospace, monospace',
  },
  {
    id: "dejavu",
    label: "DejaVu Sans Mono",
    stack: '"DejaVu Sans Mono", "Bitstream Vera Sans Mono", "Liberation Mono", ui-monospace, monospace',
  },
  {
    id: "fantasque",
    label: "Fantasque Sans Mono",
    stack: '"FantasqueSansM Nerd Font", "Fantasque Sans Mono", ui-monospace, monospace',
  },
];

/** Die Stapel mit fester Breite — für das Terminal, das nichts anderes
 *  gebrauchen kann. */
export const MONO_IDS = new Set([
  "mono", "firacode", "jetbrains", "meslo", "hack", "cascadia", "ibmplex",
  "dejavu", "fantasque",
]);

export type Darstellung = {
  schrift: string;
  /** Die Schrift des Terminals — getrennt von der übrigen Oberfläche.
   *
   *  Getrennt, weil ein Terminal andere Ansprüche stellt: es braucht feste
   *  Breite, und es braucht die Powerline- und Rahmenzeichen, die tmux und
   *  zellij zeichnen. Fehlen die in der Schrift, greift der Browser auf eine
   *  Ersatzschrift zurück, deren Glyphen breiter sind als die Zelle — und
   *  ragen in die Nachbarn. Deshalb steht hier ein eigener Wert und nicht
   *  „nimm halt die von oben". */
  terminalSchrift: string;
  /** 0…1. Wie kräftig eine Phase den Kalender einfärbt. */
  phase: number;
  /** Speichert der Editor von selbst?
   *
   *  Die Vorgabe, nicht der Zwang: im Editor selbst lässt sich das je Datei
   *  umschalten. Wer lieber bewusst speichert, stellt es hier einmal ab und
   *  bekommt es nicht bei jeder neuen Datei wieder untergeschoben. */
  autosave: boolean;
};

const KEY = "home.darstellung";
const VORGABE: Darstellung = {
  schrift: "system",
  // „mono" ist der Systemstapel und immer vorhanden. Wer eine Nerd-Schrift
  // installiert hat, stellt sie hier ein und bekommt saubere Trennlinien.
  terminalSchrift: "mono",
  phase: 0.5,
  autosave: true,
};

function lesen(): Darstellung {
  try {
    const roh = localStorage.getItem(KEY);
    if (!roh) return VORGABE;
    const d = JSON.parse(roh) as Partial<Darstellung>;
    return {
      schrift: SCHRIFTEN.some((s) => s.id === d.schrift) ? d.schrift! : VORGABE.schrift,
      phase: typeof d.phase === "number" && d.phase >= 0 && d.phase <= 1
        ? d.phase : VORGABE.phase,
      terminalSchrift: SCHRIFTEN.some((s) => s.id === d.terminalSchrift)
        ? d.terminalSchrift! : VORGABE.terminalSchrift,
      autosave: typeof d.autosave === "boolean" ? d.autosave : VORGABE.autosave,
    };
  } catch {
    return VORGABE;
  }
}

let aktuell: Darstellung = lesen();
const abonnenten = new Set<() => void>();

export function anwenden(d: Darstellung) {
  aktuell = d;
  const wurzel = document.documentElement;
  const s = SCHRIFTEN.find((x) => x.id === d.schrift) ?? SCHRIFTEN[0];
  wurzel.style.setProperty("--sans", s.stack);
  // Auch als Variable, damit das Terminal sie nicht durchgereicht bekommen
  // muss — es liest sie beim Aufbau aus.
  const t = SCHRIFTEN.find((x) => x.id === d.terminalSchrift) ?? SCHRIFTEN[0];
  wurzel.style.setProperty("--terminal-schrift", t.stack);
  wurzel.style.setProperty("--phase-staerke", String(d.phase));
  try { localStorage.setItem(KEY, JSON.stringify(d)); } catch { /* egal */ }
  for (const cb of abonnenten) cb();
}

// Einmal beim Laden, damit der erste Anblick schon stimmt.
anwenden(aktuell);

function abonnieren(cb: () => void) {
  abonnenten.add(cb);
  return () => { abonnenten.delete(cb); };
}

export function useDarstellung() {
  const wert = useSyncExternalStore(abonnieren, () => aktuell, () => aktuell);
  const setzen = useCallback(
    (teil: Partial<Darstellung>) => anwenden({ ...aktuell, ...teil }), []);
  return { darstellung: wert, setzen };
}
